import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { Card } from "../../types/chat";
import {
  KpiRowView, AttendanceChartView, StatusCompositionView, UtilizationView,
  RollupComparisonView, TasksLoadView, LeaveCoverageView, OccupancyHeatmapView,
  InsightsPanelView,
} from "../../../../components/analytics";
import type { AnalyticsDashboard } from "../../../../components/analytics";

interface Props {
  card: Card;
}

interface ReportSection {
  kind: string;
  title: string;
  synopsis?: string;
  data?: Record<string, unknown>;
  error?: string;
}

interface ReportShape {
  title?: string;
  scope?: string;
  from?: string;
  to?: string;
  group_by?: string;
  caveat?: string;
  dashboard_url?: string;
  kpis?: Record<string, number> | null;
  sections?: ReportSection[];
}

// Section kind → presentational View. Unknown kinds are skipped.
function renderSection(section: ReportSection) {
  if (section.error || !section.data) return null; // handled by the error-note branch
  // The report card owns a single footer caveat; strip each section's own
  // copy so its View doesn't render a second, duplicate caveat line.
  const data = { ...section.data, caveat: undefined } as never; // envelope shape validated server-side
  switch (section.kind) {
    case "attendance": return <AttendanceChartView data={data} />;
    case "status_composition": return <StatusCompositionView data={data} />;
    case "utilization": return <UtilizationView data={data} />;
    case "rollup": return <RollupComparisonView data={data} />;
    case "tasks": return <TasksLoadView data={data} />;
    case "leave": return <LeaveCoverageView data={data} />;
    case "occupancy": return <OccupancyHeatmapView data={data} />;
    case "insights": return <InsightsPanelView data={data} />;
    default: return null;
  }
}

/** Turn the absolute dashboard_url into an in-app path+search. */
function toInAppPath(url?: string): string {
  if (!url) return "/homework/analytics";
  try {
    const u = new URL(url);
    return `${u.pathname}${u.search}`;
  } catch {
    return url.startsWith("/") ? url : "/homework/analytics";
  }
}

export function ReportCard({ card }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  const report = (card.data.report as ReportShape) ?? {};
  const sections = report.sections ?? [];
  const dashPath = toInAppPath(report.dashboard_url);

  const handleExport = async () => {
    if (!contentRef.current) return;
    setExporting(true);
    try {
      const { exportReportToPdf } = await import("../../lib/reportPdf");
      await exportReportToPdf(contentRef.current, {
        title: report.title ?? "office-report", from: report.from, to: report.to,
      });
    } catch {
      toast.error(t("assistant.report.exportFailed"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="rounded-lg border bg-card p-3" role="region" aria-label={t("assistant.report.cardLabel")}>
      <div ref={contentRef} className="flex flex-col gap-3">
        <div>
          <p className="text-sm font-medium">{report.title ?? t("assistant.report.defaultTitle")}</p>
          {(report.from || report.to) && (
            <p className="text-[11px] text-muted-foreground">
              {report.from} – {report.to}
            </p>
          )}
        </div>

        {report.kpis && (
          // The report card owns a single footer caveat; suppress KpiRowView's
          // own (unconditional) caveat line so it isn't shown twice.
          <KpiRowView
            data={{ kpis: report.kpis, caveat: undefined } as unknown as AnalyticsDashboard}
          />
        )}

        {sections.map((section, i) => {
          if (section.error) {
            return (
              <div key={i} data-report-block
                   className="rounded-lg border border-border/60 bg-muted/20 p-3">
                <p className="text-[13px] font-medium">{section.title}</p>
                <p className="text-[12px] text-muted-foreground">
                  {t("assistant.report.sectionError", { reason: section.error })}
                </p>
              </div>
            );
          }
          const view = renderSection(section);
          if (!view) return null; // unknown kind — skipped
          return <div key={i} data-report-block>{view}</div>;
        })}

        {report.caveat && (
          <p className="text-[11px] text-muted-foreground">{report.caveat}</p>
        )}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <a
          href={dashPath}
          onClick={(e) => { e.preventDefault(); navigate(dashPath); }}
          className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[12px] hover:bg-muted"
        >
          <ExternalLink className="size-3.5" /> {t("assistant.report.openDashboard")}
        </a>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting}
          className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[12px] hover:bg-muted disabled:opacity-60"
        >
          <Download className="size-3.5" />
          {exporting ? t("assistant.report.exporting") : t("assistant.report.exportPdf")}
        </button>
      </div>
    </div>
  );
}
